import { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { LookupCrudPage } from './LookupCrudPage';
import { LookupFieldConfig } from './LookupFormDialog';
import { listLookup, LookupRecord } from '../../api/masterData';

const nameField: LookupFieldConfig = { name: 'name', label: 'Name', type: 'text', required: true };
const sortOrderField: LookupFieldConfig = { name: 'sortOrder', label: 'Sort order', type: 'number' };
const nameColumn: GridColDef<LookupRecord> = { field: 'name', headerName: 'Name', flex: 1 };

export const AcademicLevelsPage = () => (
  <LookupCrudPage
    title="Academic Levels"
    basePath="/master-data/academic-levels"
    fields={[nameField, sortOrderField]}
    extraColumns={[nameColumn]}
    icon={SchoolOutlinedIcon}
    color="#12B76A"
  />
);

export const LeadSourcesPage = () => (
  <LookupCrudPage
    title="Lead Sources"
    basePath="/master-data/lead-sources"
    fields={[nameField, sortOrderField]}
    extraColumns={[nameColumn]}
    icon={ContactsOutlinedIcon}
    color="#5B2DA3"
  />
);

export const LeadStatusesPage = () => (
  <LookupCrudPage
    title="Lead Statuses"
    basePath="/master-data/lead-statuses"
    fields={[
      nameField,
      { name: 'colorHex', label: 'Color (hex)', type: 'color' },
      sortOrderField,
      { name: 'isFinal', label: 'Final status (e.g. Flown, Lost)', type: 'boolean' },
    ]}
    extraColumns={[
      nameColumn,
      {
        field: 'colorHex',
        headerName: 'Color',
        width: 100,
        renderCell: (params) => (
          <span
            style={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: String(params.value),
              border: '1px solid rgba(0,0,0,0.2)',
            }}
          />
        ),
      },
      { field: 'isFinal', headerName: 'Final', width: 90, type: 'boolean' },
    ]}
    icon={LabelOutlinedIcon}
    color="#5B2DA3"
  />
);

/** Doubles as the "Payment Checklist" Master Data (PA/Payment/Document
 * Checklist correction) — amount/currency/description/required are new,
 * additive fields on the same existing PaymentCategory lookup. */
/** How a fee counts in Management Reports revenue — only IMC, welfare and
 * FIS exam fees are IMC revenue; everything else is collected for others. */
const REVENUE_ROLE_OPTIONS = [
  { value: 'IMC_FEE', label: 'IMC fee (revenue)' },
  { value: 'WELFARE_FEE', label: 'Welfare fee (revenue)' },
  { value: 'FIS_FEE', label: 'FIS exam fee (revenue)' },
  { value: 'PASS_THROUGH', label: 'Pass-through (not IMC revenue)' },
];

export const PaymentCategoriesPage = () => {
  const currenciesQuery = useQuery({
    queryKey: ['lookup', '/master-data/currencies'],
    queryFn: () => listLookup('/master-data/currencies', { activeOnly: true }),
  });
  const currencyOptions = (currenciesQuery.data ?? []).map((c) => ({
    value: c.id as number,
    label: `${c.code as string} (${c.name as string})`,
  }));

  return (
    <LookupCrudPage
      title="Payment Categories"
      basePath="/master-data/payment-categories"
      fields={[
        nameField,
        { name: 'amount', label: 'Required amount (leave blank for none)', type: 'number' },
        { name: 'defaultCurrencyId', label: 'Currency', type: 'select', options: currencyOptions },
        { name: 'isRequired', label: 'Required for every student', type: 'boolean' },
        { name: 'description', label: 'Description', type: 'textarea' },
        sortOrderField,
        // SSCL tax / exchange-rate payment overhaul (Phase 79)
        { name: 'isTaxable', label: 'SSCL applies to this item', type: 'boolean' },
        { name: 'isRegistrationRequirement', label: 'Counts toward registration Required Total', type: 'boolean' },
        // FIS Exam (Phase 93)
        { name: 'foundationExamOnly', label: 'Only for Foundation Exam students', type: 'boolean' },
        // Management Reports: how the fee counts in revenue.
        { name: 'reportingRole', label: 'Revenue role (Management Reports)', type: 'select', required: true, options: REVENUE_ROLE_OPTIONS },
      ]}
      extraColumns={[
        nameColumn,
        { field: 'amount', headerName: 'Amount', width: 110 },
        { field: 'isRequired', headerName: 'Required', width: 100, type: 'boolean' },
        { field: 'isTaxable', headerName: 'SSCL', width: 90, type: 'boolean' },
        { field: 'isRegistrationRequirement', headerName: 'Registration item', width: 140, type: 'boolean' },
        { field: 'foundationExamOnly', headerName: 'Foundation only', width: 130, type: 'boolean' },
        {
          field: 'reportingRole',
          headerName: 'Revenue role',
          width: 170,
          valueFormatter: (value: string) => REVENUE_ROLE_OPTIONS.find((o) => o.value === value)?.label ?? value,
        },
      ]}
      icon={PaymentsOutlinedIcon}
      color="#12B76A"
    />
  );
};

/** Doubles as the "Document Checklist" Master Data — description/
 * isRequired are new, additive fields on the same existing DocumentType
 * lookup (see docs/CHANGELOG.md for why this reuses rather than
 * duplicates the existing document-type infrastructure). */
export const DocumentTypesPage = () => (
  <LookupCrudPage
    title="Document Types"
    basePath="/master-data/document-types"
    fields={[
      nameField,
      { name: 'requiresExpiry', label: 'Has an expiry date', type: 'boolean' },
      { name: 'isRequired', label: 'Required', type: 'boolean' },
      { name: 'description', label: 'Description / instructions', type: 'textarea' },
      sortOrderField,
    ]}
    extraColumns={[
      nameColumn,
      { field: 'requiresExpiry', headerName: 'Has expiry', width: 110, type: 'boolean' },
      { field: 'isRequired', headerName: 'Required', width: 100, type: 'boolean' },
    ]}
    icon={DescriptionOutlinedIcon}
    color="#2F6FED"
  />
);

export const VisaStatusesPage = () => (
  <LookupCrudPage
    title="Visa Statuses"
    basePath="/master-data/visa-statuses"
    fields={[nameField, sortOrderField]}
    extraColumns={[nameColumn]}
    icon={AssignmentIndOutlinedIcon}
    color="#0BA5EC"
  />
);

export const TaskTypesPage = () => (
  <LookupCrudPage
    title="Task Types"
    basePath="/master-data/task-types"
    fields={[nameField, sortOrderField]}
    extraColumns={[nameColumn]}
    icon={ChecklistOutlinedIcon}
    color="#8354C6"
  />
);

export const CurrenciesPage = () => (
  <LookupCrudPage
    title="Currencies"
    basePath="/master-data/currencies"
    fields={[
      { name: 'code', label: 'ISO code (e.g. USD)', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'symbol', label: 'Symbol', type: 'text' },
    ]}
    extraColumns={[
      { field: 'code', headerName: 'Code', width: 100 },
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'symbol', headerName: 'Symbol', width: 100 },
    ]}
    icon={AttachMoneyOutlinedIcon}
    color="#F5A623"
  />
);

export const IntakesPage = () => (
  <LookupCrudPage
    title="Intakes"
    basePath="/master-data/intakes"
    fields={[
      nameField,
      { name: 'month', label: 'Month (1-12)', type: 'number', required: true },
      { name: 'year', label: 'Year', type: 'number', required: true },
    ]}
    extraColumns={[nameColumn, { field: 'month', headerName: 'Month', width: 90 }, { field: 'year', headerName: 'Year', width: 90 }]}
    icon={EventOutlinedIcon}
    color="#2F6FED"
  />
);

export const CountriesPage = () => (
  <LookupCrudPage
    title="Countries"
    basePath="/master-data/countries"
    fields={[
      nameField,
      { name: 'iso2', label: 'ISO2 code', type: 'text', required: true },
      { name: 'iso3', label: 'ISO3 code', type: 'text' },
      { name: 'phoneCode', label: 'Phone code', type: 'text' },
    ]}
    extraColumns={[
      nameColumn,
      { field: 'iso2', headerName: 'ISO2', width: 80 },
      { field: 'phoneCode', headerName: 'Phone code', width: 110 },
    ]}
    icon={PublicOutlinedIcon}
    color="#12B76A"
  />
);

export const EmailTemplatesPage = () => (
  <LookupCrudPage
    title="Email Templates"
    basePath="/master-data/email-templates"
    fields={[
      nameField,
      { name: 'subject', label: 'Subject', type: 'text', required: true },
      { name: 'body', label: 'Body (HTML)', type: 'textarea', required: true },
      { name: 'category', label: 'Category', type: 'text' },
    ]}
    extraColumns={[
      nameColumn,
      { field: 'subject', headerName: 'Subject', flex: 1 },
      { field: 'category', headerName: 'Category', width: 160 },
    ]}
    icon={EmailOutlinedIcon}
    color="#7F56D9"
  />
);

export const WhatsappTemplatesPage = () => (
  <LookupCrudPage
    title="WhatsApp Templates"
    basePath="/master-data/whatsapp-templates"
    fields={[
      nameField,
      { name: 'body', label: 'Message body', type: 'textarea', required: true },
      { name: 'category', label: 'Category', type: 'text' },
    ]}
    extraColumns={[nameColumn, { field: 'category', headerName: 'Category', width: 160 }]}
    icon={WhatsAppIcon}
    color="#25D366"
  />
);
